import { useState, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { Box, Button, Chip, Typography, IconButton } from '@mui/material'
import { UploadContractDialog } from './UploadContractDialog'
import { Company } from '@/@data/company'
import { useRouteQuery, useTranslator } from '@/hooks'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { EmployerContractHistoryFilter } from './EmployerContractHistoryFilter'
import { TablePagination } from '@/@core/components/mui/TablePagination'

export const EmployerContractHistory = () => {
  const [openUploadContractDialog, setOpenUploadContractDialog] = useState(false)
  const { __ } = useTranslator()
  const { searchQuery } = useRouteQuery()
  const { id } = useParams()

  const paramsContractHistory = useMemo(() => {
    return {
      companyId: Number(id),
      page: searchQuery.get('page'),
      perPage: searchQuery.get('perPage'),
      keywords: searchQuery.get('keyword')
    }
  }, [id, searchQuery])

  const { data: dataContractHistory, refetch } = useQuery({
    ...Company.getContractHistory(Number(id), paramsContractHistory)
  })

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return { label: 'Active', color: '#28C76F' }
      case 'termination_pending':
        return { label: 'Termination Pending', color: '#FF9F43' }
      case 'expired':
        return { label: 'Expired', color: '#FF4C51' }
      case 'disable':
        return { label: 'Disable', color: '#333' }
      default:
        return { label: 'Unknown', color: '#6E6B7B' }
    }
  }

  const getBackgroundColor = (status: string) => {
    switch (status) {
      case 'active':
        return '#DDF6E8'
      case 'termination_pending':
        return '#FDF0E1'
      case 'expired':
        return '#FCE2E3'
      case 'disable':
        return '#EEEEEF'
      default:
        return '#6E6B7B'
    }
  }

  return (
    <Box className='flex flex-col gap-6 p-6'>
      <div className='flex flex-col gap-4'>
        <Box className='flex items-center justify-between'>
          <Typography className='text-[18px] leading-[28px] font-medium'>{__('Contract History')}</Typography>
          <Button
            variant='contained'
            className='text-[#3551F2] bg-[#E8E6FC] shadow-none rounded-full'
            startIcon={<i className='tabler-upload' />}
            onClick={() => setOpenUploadContractDialog(true)}
          >
            {__('Upload Active Contract')}
          </Button>
        </Box>
        <Typography className='text-[15px] leading-[22px] font-normal '>
          {__(
            'This will store all your previous contracts, and you can review the information of your past contracts anytime.'
          )}
        </Typography>
      </div>
      <EmployerContractHistoryFilter />
      <div className='overflow-x-auto border rounded-[20px]'>
        <table className='w-full border-collapse'>
          <thead>
            <tr className='border-b'>
              <th className='p-4 text-left text-[13px] font-medium leading-[24px] space-[0.2px] uppercase'>#</th>
              <th className='p-4 text-left text-[13px] font-medium leading-[24px] space-[0.2px] uppercase'>
                {__('Contract File name')}
              </th>
              <th className='p-4 text-left text-[13px] font-medium leading-[24px] space-[0.2px] uppercase'>
                {__('Start date')}
              </th>
              <th className='p-4 text-left text-[13px] font-medium leading-[24px] space-[0.2px] uppercase'>
                {__('End date')}
              </th>
              <th className='p-4 text-left text-[13px] font-medium leading-[24px] space-[0.2px] uppercase'>
                {__('Status')}
              </th>
              <th className='p-4 text-left text-[13px] font-medium leading-[24px] space-[0.2px] uppercase'>
                {__('Action')}
              </th>
            </tr>
          </thead>
          <tbody>
            {dataContractHistory?.data.map((item: any, index: number) => (
              <tr className='border-b border-gray-100' key={item.id}>
                <td className='p-4 text-[15px] font-normal leading-[22px] '>{index + 1}</td>
                <td className='p-4 text-[15px] font-normal leading-[22px] '>{item.file?.name}</td>
                <td className='p-4 text-left text-[15px] font-normal leading-[22px]'>
                  {dayjs(item.start_date).format('DD MMM YYYY')}
                </td>
                <td className='p-4 text-left text-[15px] font-normal leading-[22px]'>
                  {dayjs(item.end_date).format('DD MMM YYYY')}
                </td>
                <td className='p-4'>
                  <Chip
                    label={getStatusColor(item.status)?.label}
                    className='h-[24px] rounded-full'
                    sx={{
                      backgroundColor: getBackgroundColor(item.status),
                      color: getStatusColor(item.status)?.color
                    }}
                  />
                </td>
                <td className='p-4'>
                  <Box className='flex items-center gap-2'>
                    <IconButton onClick={() => window.open(item.file?.url, '_blank')}>
                      <i className='tabler-eye' />
                    </IconButton>
                    <IconButton
                      onClick={async () => {
                        if (item?.file?.url) {
                          try {
                            const response = await fetch(item.file?.url)
                            const blob = await response.blob()
                            const url = window.URL.createObjectURL(blob)

                            const link = document.createElement('a')

                            link.href = url
                            link.download = item.file?.name || 'contract-file'
                            document.body.appendChild(link)
                            link.click()

                            document.body.removeChild(link)
                            window.URL.revokeObjectURL(url)
                          } catch (error) {
                            window.open(item.file?.url, '_blank')
                          }
                        }
                      }}
                    >
                      <i className='tabler-download' />
                    </IconButton>
                  </Box>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {dataContractHistory?.meta && (
        <TablePagination
          meta={{
            ...dataContractHistory.meta,
            page: Number(searchQuery.get('page')) || 1
          }}
        />
      )}
      <UploadContractDialog
        open={openUploadContractDialog}
        onClose={() => setOpenUploadContractDialog(false)}
        refetch={refetch}
      />
    </Box>
  )
}
